<script setup lang="ts">
/** Step 5 · Pack (plan §10): the build pack the maker will read, and the end of the journey. */
import { computed } from 'vue';
import { lastExportChanged, packModel } from '@/export';
import Icon from './Icon.vue';
import StepPanel from './StepPanel.vue';
import PanelHead from './PanelHead.vue';
import ElevationSvg from './ElevationSvg.vue';
import CutTable from './CutTable.vue';
import FixActions from './FixActions.vue';
import { app, snap, ui } from '@/app/state';
import { announce } from '@/app/announce';
import { changeLabel, exportBlocker, nextRevId } from '@/app/navigation';
import { exportPack, type ExportKindUi } from '@/app/exports';
import { fmtDate, short } from '@/app/format';
import { checkState, runCheck } from '@/app/checks';
import { flushSave } from '@/app/persistence';

const s = computed(() => snap.value);
const pack = computed(() => packModel(s.value.build, { revisionId: s.value.currentRevisionId, dirty: s.value.isDirty, fields: s.value.fields }, { construction: s.value.construction }));
const block = computed(() => { void s.value; return exportBlocker(); });
const changed = computed(() => { void s.value; return lastExportChanged(app.store); });
const KIND: Record<string, string> = { csv: 'CSV', json: 'JSON', print: 'Print / PDF' };
const exportHelp = computed(() => (s.value.isDirty || !s.value.currentRevision ? `Exporting saves your edits as ${nextRevId()} first.` : `Exporting uses ${s.value.currentRevisionId} as it is.`));
const stampCls = computed(() => (pack.value.stamp.kind !== 'draft' ? 'stamp--draft' : pack.value.watermark ? 'stamp--prov' : 'stamp--ok'));
const stampTitle = computed(() => (pack.value.stamp.kind === 'unconfirmed' ? pack.value.stamp.title : pack.value.stamp.title));

function onExport(e: MouseEvent, kind: ExportKindUi) {
  if ((e.currentTarget as HTMLElement).getAttribute('aria-disabled') === 'true') { announce(`Not available: ${block.value}`); return; }
  void exportPack(kind);
}
function openSheet() { ui.exportSheetOpen = true; }
/** The independent check (Phase 3): derived from receipts, a held repair first. */
const check = computed(() => { void s.value; void ui.checks; void ui.checking; return checkState(); });
async function checkNow() {
  if (!ui.projectId || block.value) { if (block.value) announce(`Not available: ${block.value}`); return; }
  const r = app.store.ensureRevision(changeLabel('Checked'));
  await flushSave();
  const receipt = await runCheck(ui.projectId, r.id);
  if (receipt) announce(receipt.status === 'passed' ? `${r.id} passed the independent check.` : `The independent check found a problem in ${r.id}.`);
}
</script>

<template>
  <StepPanel>
    <PanelHead :n="5" title="Build pack" eyebrow="Step 5 of 5 · final" />
    <div class="stamp" :class="stampCls">
      <Icon :name="pack.stamp.kind === 'draft' && !pack.watermark ? 'check' : 'alert'" />
      <div>
        <strong>{{ stampTitle }}</strong>
        <span>{{ pack.stamp.kind === 'draft' ? `Give this pack to your maker for revision ${s.currentRevisionId ?? nextRevId()}${s.isDirty || !s.currentRevision ? ` (your edits are saved as ${nextRevId()} when you export)` : ''} · ${pack.hashShort}.` : pack.stamp.text }}</span>
        <template v-if="pack.watermark">
          <strong class="stamp-water">{{ pack.watermark }}</strong>
          <span>{{ pack.provisionalNote }}</span>
        </template>
      </div>
    </div>
    <!-- shelf-board-assembly plan Phase 3: the independent check of joints, supports and stock fit -->
    <p class="notes check-status" :class="check.kind === 'failed' || check.kind === 'held' ? 'notes--err' : 'notes--plain'" :data-check="check.kind">
      <Icon :name="check.kind === 'passed' ? 'check' : check.kind === 'failed' || check.kind === 'held' ? 'alert' : 'info'" />
      <span v-if="check.kind === 'held'">The rebuild's changes are held for your review in Check. Accept them or adjust the layout before the independent check and export.</span>
      <span v-else-if="check.kind === 'checking'">Checking {{ check.revisionId }}: joints, supports and stock fit…</span>
      <span v-else-if="check.kind === 'passed'">Independently checked: {{ check.receipt.revisionId }}'s joints, supports and stock fit agree with the cut list.<template v-if="check.receipt.warnings.length"> {{ check.receipt.warnings.join(' ') }}</template> This is a geometry check, not a load rating.</span>
      <span v-else-if="check.kind === 'failed'">The independent check found {{ check.receipt.errors.length === 1 ? 'a problem' : `${check.receipt.errors.length} problems` }} in {{ check.receipt.revisionId }}: {{ check.receipt.errors.slice(0, 3).join('; ') }}. Exports stay blocked until it passes.</span>
      <span v-else>Not checked yet. The independent check of joints, supports and stock fit runs when you export, or now.</span>
      <button v-if="check.kind === 'unchecked' || check.kind === 'failed'" type="button" class="btn btn--sm btn--quiet check-now" data-act="check-now" :aria-disabled="block ? 'true' : undefined" @click="checkNow">Check now</button>
    </p>
    <p v-if="changed" class="notes notes--plain export-changed-top"><Icon name="alert" /><span>Your design has changed since the last export ({{ changed.revisionId }} · <span class="mono">{{ short(changed.manufacturingHash) }}</span>).</span></p>
    <div class="sheet-head">
      <div><span class="muted small">Revision</span><strong>{{ pack.revisionText }}</strong></div>
      <div><span class="muted small">Manufacturing hash</span><strong class="mono">{{ pack.hashShort }}</strong></div>
      <div><span class="muted small">Overall</span><strong class="mono">{{ pack.overallMm.W }} × {{ pack.overallMm.H }} × {{ pack.overallMm.D }}</strong></div>
      <div><span class="muted small">Panels</span><strong>{{ pack.panelCount }}</strong></div>
    </div>
    <div v-if="pack.errors.length" class="notes notes--err"><p v-for="e in pack.errors" :key="e"><Icon name="alert" /><span>{{ e }}</span></p></div>
    <div v-if="pack.warnings.length" class="notes" role="group" aria-label="Advice"><p v-for="w in pack.warnings" :key="w"><Icon name="alert" /><span>{{ w }}</span></p></div>
    <section class="sec pack-split" aria-labelledby="elevH">
      <div>
        <h2 id="elevH" class="h-sec">Front elevation</h2>
        <div id="elevWrap" class="elev-wrap"><ElevationSvg :build="s.build" /></div>
      </div>
      <div>
        <h2 id="datumsH" class="h-sec">Shelf datums</h2>
        <p class="muted small">Undersides, measured from the floor. Mark them from the same end on both {{ s.build.bays.length > 1 ? 'verticals of each bay' : 'sides' }}.</p>
        <ol class="datums" aria-labelledby="datumsH">
          <li v-for="dt in pack.datums.slice().reverse()" :key="dt.partId"><span>{{ dt.label }}</span><strong class="mono">{{ dt.text }}</strong></li>
          <li v-if="!pack.datums.length"><span>No shelves</span></li>
        </ol>
        <h2 class="h-sec h-sec--gap">Stock fit</h2>
        <p class="fit" :class="pack.sheetFit.allFit ? 'is-ok' : 'is-err'"><Icon :name="pack.sheetFit.allFit ? 'check' : 'alert'" /><span>{{ pack.sheetFit.text }}</span></p>
        <p class="muted small">{{ pack.sheetFit.notes[0] }}</p>
      </div>
    </section>
    <section class="sec" aria-labelledby="cutH">
      <div class="sec-head"><h2 id="cutH" class="h-sec">Cut list <span class="muted">finished sizes, mm, by material</span></h2></div>
      <template v-for="g in pack.cutGroups" :key="g.stockId">
        <h3 class="h-sub cut-group" :data-group="g.stockId">{{ g.title }}</h3>
        <div class="table-wrap"><CutTable :build="s.build" :part-ids="g.rows.map((r) => r.id)" :caption="`Cut list: ${g.title}`" /></div>
      </template>
      <p class="muted small">{{ pack.cutCaption }}</p>
      <template v-if="pack.glueUps.length">
        <h3 class="h-sub">Glue-ups</h3>
        <ul class="glueups"><li v-for="g in pack.glueUps" :key="g">{{ g }}</li></ul>
      </template>
      <p v-if="pack.massText" class="muted small" data-note="mass">{{ pack.massText }}</p>
    </section>
    <section v-if="pack.fronts" class="sec" aria-labelledby="frontsH" data-pack="fronts">
      <div class="sec-head"><h2 id="frontsH" class="h-sec">Doors and drawers</h2><span class="tag">{{ pack.fronts.summary }}</span></div>
      <dl class="kv kv--long">
        <div v-for="h in pack.fronts.hardware" :key="h.kind"><dt>{{ h.qty }} × {{ h.kind }}</dt><dd>{{ h.spec }}</dd></div>
        <div v-if="pack.fronts.glass.length"><dt>Glass to order</dt><dd><span v-for="g in pack.fronts.glass" :key="g">{{ g }}<br></span></dd></div>
      </dl>
      <details class="steps-det">
        <summary>Fitting, {{ pack.fronts.fitting.length }} notes</summary>
        <ol class="assembly"><li v-for="(f, i) in pack.fronts.fitting" :key="i">{{ f }}</li></ol>
      </details>
    </section>
    <section class="sec" aria-labelledby="profH">
      <div class="sec-head"><h2 id="profH" class="h-sec">Construction rules</h2><span class="tag tag--prov">{{ pack.profile.tag }}</span></div>
      <p class="muted small">{{ pack.profile.name }} · {{ pack.profile.id }}. {{ pack.profile.note }}</p>
      <dl class="kv kv--long">
        <div v-for="r in pack.profile.rows" :key="r.label"><dt>{{ r.label }}</dt><dd>{{ r.text }}</dd></div>
      </dl>
      <details class="steps-det">
        <summary>Assembly, {{ pack.profile.assemblySteps.length }} steps</summary>
        <ol class="assembly"><li v-for="(st, i) in pack.profile.assemblySteps" :key="i">{{ st }}</li></ol>
      </details>
    </section>
    <section class="sec" aria-labelledby="expH">
      <h2 id="expH" class="h-sec">Export</h2>
      <div class="exports">
        <button type="button" class="btn btn--quiet" data-act="csv" :aria-disabled="pack.exports.csv.enabled ? undefined : 'true'" @click="onExport($event, 'csv')"><Icon name="download" /> Cut list CSV</button>
        <button type="button" class="btn btn--quiet" data-act="json" @click="onExport($event, 'json')"><Icon name="download" /> Project JSON</button>
        <button type="button" class="btn btn--quiet" data-act="print" :aria-disabled="pack.exports.print.enabled ? undefined : 'true'" @click="onExport($event, 'print')"><Icon name="print" /> Print / Save as PDF</button>
      </div>
      <p class="muted small">{{ block ? `${s.build.validation.ok ? 'CSV and print are off until the critical values are confirmed.' : 'CSV and print are off while the design is invalid. JSON stays available and says validation.ok is false.'} ${block}` : `${exportHelp} Every export carries the revision and manufacturing hash.` }}</p>
      <p v-if="changed" class="notes notes--plain export-changed"><Icon name="alert" /><span>Your design has changed since the last export ({{ changed.revisionId }} · <span class="mono">{{ short(changed.manufacturingHash) }}</span>). Export again to send the current design.</span></p>
      <h3 class="h-sec h-sec--gap">Exports so far</h3>
      <ol v-if="s.exports.length" class="export-log" aria-label="Exports of this project">
        <li v-for="e in s.exports.slice(0, 8)" :key="e.at + e.kind">Exported <strong>{{ e.revisionId }}</strong> · <span class="mono">{{ short(e.manufacturingHash) }}</span> · {{ KIND[e.kind] ?? e.kind }} · <time :datetime="e.at">{{ fmtDate(e.at) }}</time></li>
      </ol>
      <p v-else class="muted small">Nothing exported yet.</p>
    </section>

    <template #dock>
      <FixActions v-if="!s.build.validation.ok" where="pack" />
      <p v-else-if="block" class="dock-note dock-note--err"><Icon name="alert" /> {{ block }}</p>
      <span class="dock-btn-wrap">
        <button id="dock-export" type="button" class="btn btn--lg" :class="s.build.validation.ok ? 'btn--ink' : 'btn--quiet'" data-act="export-sheet" aria-haspopup="dialog" aria-describedby="exportHelp" @click="openSheet"><Icon name="download" /> Export the pack</button>
        <small id="exportHelp" class="dock-help">{{ block ? 'JSON only until this is fixed.' : exportHelp }}</small>
      </span>
    </template>
  </StepPanel>
</template>

<style scoped>
.check-status { flex-direction: row; align-items: flex-start; margin: 0 0 16px; }
.check-status span { flex: 1; }
.check-now { flex: none; margin-left: auto; }
</style>
